import React, { useEffect, useMemo, useState } from 'react';
import { PauseIcon, PlayIcon, SquareIcon } from 'lucide-react';
import { Employee, ProJob, RunningTimer, TimeEntry, WorkType } from '../../types/pro';
import { addDays, formatDuration, startOfWeek, toISODate } from '../../utils/pro';
import { PageHeader } from '../../components/pro/PageHeader';
import { JobPicker } from '../../components/pro/JobPicker';
import { TimeEntryList } from '../../components/pro/TimeEntryList';
interface ProTimeProps {
  jobs: ProJob[];
  employees: Employee[];
  timeEntries: TimeEntry[];
  onAdd: (entry: Omit<TimeEntry, 'id'>) => void;
  onUpdate: (id: string, changes: Partial<TimeEntry>) => void;
  onRemove: (id: string) => void;
}
const STORAGE_KEY = 'proRunningTimer';
const WORK_TYPES: WorkType[] = ['Montage', 'Fejlfinding', 'Service', 'Kørsel', 'Andet'];

/** Jobs you'd realistically book time on — finished work is hidden. */
const isBookable = (job: ProJob) => job.status !== 'faktureret' && job.status !== 'faerdig';
export function ProTime({
  jobs,
  employees,
  timeEntries,
  onAdd,
  onUpdate,
  onRemove
}: ProTimeProps) {
  const [timer, setTimer] = useState<RunningTimer | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) as RunningTimer : null;
    } catch {
      return null;
    }
  });
  const [, forceTick] = useState(0);
  // A session runs against whatever was picked when it started, so the
  // pickers follow the timer rather than the other way round.
  const [employeeId, setEmployeeId] = useState(() => timer?.employeeId ?? '');
  const [jobId, setJobId] = useState(() => timer?.jobId ?? '');
  const [workType, setWorkType] = useState<WorkType>(() => timer?.workType ?? 'Montage');
  const [manual, setManual] = useState({
    hours: '',
    note: '',
    date: toISODate(new Date())
  });
  const [range, setRange] = useState<'today' | 'week'>('today');
  const options = useMemo(() => {
    const bookable = jobs.filter(isBookable);
    // Never hide the job a session is already running against.
    const running = jobs.find(job => job.id === timer?.jobId);
    return running && !bookable.some(job => job.id === running.id) ? [running, ...bookable] : bookable;
  }, [jobs, timer?.jobId]);
  useEffect(() => {
    if (!employeeId && employees[0]?.id) setEmployeeId(employees[0].id);
    if (!jobId && options[0]?.id) setJobId(options[0].id);
  }, [employees, options, employeeId, jobId]);

  // Tick once a second only while actually running.
  useEffect(() => {
    if (!timer?.startedAt) return;
    const id = setInterval(() => forceTick(value => value + 1), 1000);
    return () => clearInterval(id);
  }, [timer?.startedAt]);
  const persist = (next: RunningTimer | null) => {
    setTimer(next);
    try {
      if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* private browsing — the timer just won't survive a reload */
    }
  };

  /** Elapsed is derived from the start timestamp, so a reload loses nothing. */
  const elapsed = timer ? timer.bankedSeconds + (timer.startedAt ? (Date.now() - timer.startedAt) / 1000 : 0) : 0;
  const decimalHours = Math.round(elapsed / 3600 * 100) / 100;
  const start = () => {
    if (!jobId || !employeeId) return;
    persist(timer ?
    // Resuming: keep the session's own job, not whatever the pickers show.
    {
      ...timer,
      startedAt: Date.now()
    } : {
      employeeId,
      jobId,
      workType,
      startedAt: Date.now(),
      bankedSeconds: 0
    });
  };
  const pause = () => {
    if (!timer?.startedAt) return;
    persist({
      ...timer,
      bankedSeconds: elapsed,
      startedAt: null
    });
  };
  const stop = () => {
    if (!timer) return;
    if (decimalHours > 0) {
      onAdd({
        employeeId: timer.employeeId,
        jobId: timer.jobId,
        workType: timer.workType,
        date: toISODate(new Date()),
        hours: decimalHours,
        note: '',
        billable: timer.workType !== 'Kørsel'
      });
    }
    persist(null);
  };
  const discard = () => {
    if (!window.confirm('Kassér den optalte tid uden at gemme?')) return;
    persist(null);
  };
  const addManual = () => {
    const hours = parseFloat(manual.hours.replace(',', '.'));
    if (!hours || hours <= 0 || !employeeId || !jobId) return;
    onAdd({
      employeeId,
      jobId,
      workType,
      date: manual.date || toISODate(new Date()),
      hours,
      note: manual.note.trim(),
      billable: workType !== 'Kørsel'
    });
    setManual({
      hours: '',
      note: '',
      date: manual.date
    });
  };
  const today = toISODate(new Date());
  const monday = toISODate(startOfWeek(new Date()));
  const sunday = toISODate(addDays(startOfWeek(new Date()), 6));
  const mine = useMemo(() => employees.length > 1 ? timeEntries.filter(entry => entry.employeeId === employeeId) : timeEntries, [timeEntries, employees.length, employeeId]);
  const todays = mine.filter(entry => entry.date === today);
  const thisWeek = mine.filter(entry => entry.date >= monday && entry.date <= sunday);
  const todayTotal = todays.reduce((sum, entry) => sum + entry.hours, 0);
  const weekTotal = thisWeek.reduce((sum, entry) => sum + entry.hours, 0);
  const visible = range === 'today' ? todays : thisWeek;
  const runningJob = jobs.find(job => job.id === timer?.jobId);
  const isRunning = Boolean(timer?.startedAt);
  const hasJobs = options.length > 0;
  const field = 'w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500 disabled:bg-stone-50 disabled:text-stone-400';
  return <div className="mx-auto max-w-3xl" data-id="element-2197" data-mp="2ol94x5yy9wa2">
      <PageHeader title="Timer" subtitle={`${todayTotal.toLocaleString('da-DK')} timer i dag · ${weekTotal.toLocaleString('da-DK')} timer denne uge`} data-id="element-2198" data-mp="2tk2gutyl9f84" />

      <section className="rounded-xl border border-stone-200 bg-white p-6 text-center" data-id="element-2199" data-mp="2n3k8qchddfef">
        <p className="font-mono text-5xl font-bold tabular-nums tracking-tight text-stone-900 sm:text-6xl" aria-live="off" data-id="element-2200" data-mp="2i47gtdbn5i4z">
          {formatDuration(elapsed)}
        </p>

        {/* What actually gets saved, so Stop holds no surprises. */}
        <p className="mt-2 min-h-[20px] text-sm text-stone-500" data-id="element-2201" data-mp="3auzbu7gz00qq">
          {timer ? <>
              <span className="font-medium text-stone-700" data-id="element-2202" data-mp="35vv13ceeduya">
                {runningJob?.title ?? 'Opgave'}
              </span>{' '}
              · {timer.workType} ·{' '}
              {timer.startedAt ? <>gemmes som {decimalHours.toLocaleString('da-DK')} t</> : <span className="font-medium text-amber-600" data-id="element-2203" data-mp="3tl7bugpdib2p">på pause</span>}
            </> : hasJobs ? 'Vælg opgave og tryk start' : 'Opret en opgave først — tid skal bogføres på en opgave'}
        </p>

        <div className="mt-6 flex justify-center gap-3" data-id="element-2204" data-mp="1g9cydjvlmb4t">
          <button onClick={start} disabled={isRunning || !hasJobs || !jobId} className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-4 text-base font-bold text-white transition-colors hover:bg-emerald-700 disabled:bg-stone-200 disabled:text-stone-400 sm:flex-none sm:px-8" data-id="element-2205" data-mp="1kaey01tfgp7b">
            <PlayIcon className="h-5 w-5" data-id="element-2206" data-mp="1z5m8jw3dqhjj" />
            {timer && !isRunning ? 'Fortsæt' : 'Start'}
          </button>
          <button onClick={pause} disabled={!isRunning} className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-stone-200 px-5 py-4 text-base font-bold text-stone-700 transition-colors hover:bg-stone-50 disabled:text-stone-300 sm:flex-none sm:px-8" data-id="element-2207" data-mp="2800w0ly2cydq">
            <PauseIcon className="h-5 w-5" data-id="element-2208" data-mp="1su884nkd4h0z" />
            Pause
          </button>
          <button onClick={stop} disabled={!timer} className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-stone-900 px-5 py-4 text-base font-bold text-white transition-colors hover:bg-stone-800 disabled:bg-stone-200 disabled:text-stone-400 sm:flex-none sm:px-8" data-id="element-2209" data-mp="1nwzr4qayaj1b">
            <SquareIcon className="h-5 w-5" data-id="element-2210" data-mp="1a5be8ksvnqja" />
            Stop og gem
          </button>
        </div>

        {timer && <button onClick={discard} className="mt-3 text-xs font-medium text-stone-400 transition-colors hover:text-red-600" data-id="element-2211" data-mp="153vucw2frq2h">
            Kassér uden at gemme
          </button>}

        <div className={`mt-6 grid grid-cols-1 gap-3 text-left ${employees.length > 1 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`} data-id="element-2212" data-mp="qgklj1xn2k62">
          {employees.length > 1 && <label className="block" data-id="element-2213" data-mp="caqxhrtlgns6">
              <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-stone-400" data-id="element-2214" data-mp="c9poru01wddm">
                Medarbejder
              </span>
              <select value={employeeId} onChange={e => setEmployeeId(e.target.value)} disabled={Boolean(timer)} className={field} data-id="element-2215" data-mp="2dmm8n1aecq5">
                {employees.map(employee => <option key={employee.id} value={employee.id} data-id="element-2216" data-mp="uzp61upngo6h">
                    {employee.name}
                  </option>)}
              </select>
            </label>}
          <JobPicker label="Opgave" jobs={options} value={timer ? timer.jobId : jobId} onChange={setJobId} disabled={Boolean(timer)} data-id="element-2217" data-mp="l5osr5k7scmo" />
          <label className="block" data-id="element-2218" data-mp="35w64qxu5y47r">
            <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-stone-400" data-id="element-2219" data-mp="3atfteb9qabuj">
              Arbejdstype
            </span>
            <select value={timer ? timer.workType : workType} onChange={e => setWorkType(e.target.value as WorkType)} disabled={Boolean(timer)} className={field} data-id="element-2220" data-mp="3llpa2sd4do3s">
              {WORK_TYPES.map(type => <option key={type} value={type} data-id="element-2221" data-mp="3gsw646ph6jsr">
                  {type}
                </option>)}
            </select>
          </label>
        </div>

        {timer && <p className="mt-3 text-xs text-stone-400" data-id="element-2222" data-mp="37uk65e2py9nn">
            Valgene er låst mens uret kører, så tiden ikke havner på en anden
            opgave.
          </p>}
      </section>

      <section className="mt-5 rounded-xl border border-stone-200 bg-white p-5" data-id="element-2223" data-mp="2xujodwki01lk">
        <h2 className="mb-1 text-base font-semibold text-stone-900" data-id="element-2224" data-mp="r4dfq6t1ph2j">
          Manuel registrering
        </h2>
        <p className="mb-3 text-sm text-stone-500" data-id="element-2225" data-mp="w487sdok4cp6">
          Bruger opgaven og arbejdstypen valgt ovenfor.
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-[150px_110px_1fr_auto]" data-id="element-2226" data-mp="2gmsi3x6u2fy5">
          <input type="date" value={manual.date} onChange={e => setManual({
          ...manual,
          date: e.target.value
        })} aria-label="Dato" className={field} data-id="element-2227" data-mp="2bnz1vx05waua" />
          <input value={manual.hours} onChange={e => setManual({
          ...manual,
          hours: e.target.value
        })} placeholder="Timer" inputMode="decimal" aria-label="Timer" className={field} data-id="element-2228" data-mp="2ljix0h6wj5hz" />
          <input value={manual.note} onChange={e => setManual({
          ...manual,
          note: e.target.value
        })} placeholder="Note (valgfri)" className={`${field} col-span-2 sm:col-span-1`} data-id="element-2229" data-mp="39ay22hq2c0wx" />
          <button onClick={addManual} disabled={!manual.hours || !jobId} className="col-span-2 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:bg-stone-200 disabled:text-stone-400 sm:col-span-1" data-id="element-2230" data-mp="1ewx4e0691jyf">
            Tilføj
          </button>
        </div>
      </section>

      <section className="mt-5 overflow-hidden rounded-xl border border-stone-200 bg-white" data-id="element-2231" data-mp="3izgn388d2qzx">
        <header className="flex flex-wrap items-center gap-3 border-b border-stone-100 px-5 py-3" data-id="element-2232" data-mp="3o0nq6tzk7209">
          <h2 className="flex-1 text-base font-semibold text-stone-900" data-id="element-2233" data-mp="3sxmm9i2s9j13">
            Registrerede timer
          </h2>
          <div className="flex rounded-lg bg-stone-100 p-0.5" data-id="element-2234" data-mp="1qtlugftdnxm">
            {([['today', 'I dag'], ['week', 'Denne uge']] as const).map(([id, label]) => <button key={id} onClick={() => setRange(id)} aria-pressed={range === id} className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${range === id ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-2235" data-mp="fjsz0bs4j620">
                {label}
              </button>)}
          </div>
          <span className="text-sm font-semibold tabular-nums text-stone-900" data-id="element-2236" data-mp="kqg8ab0e1r50">
            {(range === 'today' ? todayTotal : weekTotal).toLocaleString('da-DK')}{' '}
            t
          </span>
        </header>
        <TimeEntryList entries={visible} jobs={jobs} emptyTitle={range === 'today' ? 'Ingen registreringer i dag' : 'Ingen registreringer i denne uge'} onUpdate={onUpdate} onRemove={onRemove} data-id="element-2237" data-mp="18dxttot27szj" />
      </section>
    </div>;
}